import { Blocks, isEditing } from "@builder.io/sdk-react";
import classNames from "classnames";
import Image from "next/image";

import { Container } from "@jti/core-components/components/Container";
import { useLoyaltyHeroDataTransform } from "@jti/loyalty/hooks";
import type { HeroV2Content, HeroV2LoyaltyContent, HeroV2Props } from "@jti/ui/components/HeroV2";
import { HeroV2 } from "@jti/ui/components/HeroV2";

import type { HeroContainerProps } from "../HeroBannerContainer/HeroBannerContainer";
import { ResponsiveBackgroundImage } from "../HeroBannerContainer/ResponsiveBackgroundImage";

import { LoyaltyHeroDataSchema } from "./schema";

import styles from "../HeroBannerContainer/HeroBannerContainer.module.scss";

// This component is a copy of HeroBannerContainer component made due to tight coupling of component. Tech debpt ticket was created to revisit hero banner like components DESP-96085
function isValidLoyaltyHeroBlock(block: BuilderBlock): block is BuilderBlock & {
  component: { name: string; options: { data: HeroV2LoyaltyContent } };
} {
  return !!block.component && !!block.component.options && typeof block.component.options.data === "object";
}

export function HeroBannerContainer({
  isContainerWidth,
  isViewportHeight,
  childBlocks,
  builderBlock,
  autoplayEnabled = false,
  autoplayDelay = "5",
  breadcrumbsSpacing = false,
}: HeroContainerProps) {
  const isChildBlocksArray = Array.isArray(childBlocks);
  const { convertToHeroData } = useLoyaltyHeroDataTransform();

  const convertedData = isChildBlocksArray
    ? childBlocks.filter(isValidLoyaltyHeroBlock).map((item, index): ArrayElement<HeroV2Props["data"]> | null => {
        const heroData = item?.component?.options.data;
        heroData.removeVideoRoundedCorners = true;

        let validatedHeroData;
        try {
          validatedHeroData = LoyaltyHeroDataSchema.parse(heroData);
        } catch (error) {
          console.error("Loyalty hero data validation failed", error);
          return null;
        }

        const properProps = convertToHeroData(validatedHeroData);

        return {
          ...properProps,
          renderBackgroundImage: (props) => <ResponsiveBackgroundImage {...props} index={index} />,
          withBreadcrumbs: breadcrumbsSpacing,
          renderImage: ({ src }: { src: string }) => (
            <Image
              src={src}
              alt={heroData.logoAlt}
              height={heroData.logoHeight ?? 63}
              width={heroData.logoWidth ?? 100}
            />
          ),
        };
      })
    : [];

  const filteredConvertedData: HeroV2Content[] = convertedData.filter((data): data is HeroV2Content => data !== null);

  if (isEditing()) {
    return <Blocks parent={builderBlock?.id} blocks={childBlocks} className={styles.editingWrapper} />;
  }

  const Wrapper = isContainerWidth ? Container : "div";

  return (
    <Wrapper
      className={classNames({
        [styles.fullWidthContainer as string]: !isContainerWidth,
        [styles.fullHeightContainer as string]: !isContainerWidth && isViewportHeight,
      })}
    >
      <HeroV2
        data={filteredConvertedData}
        isContainerWidth={isContainerWidth}
        isViewportHeight={isViewportHeight}
        autoplayEnabled={autoplayEnabled}
        autoplayDelayInSeconds={Number(autoplayDelay)}
      />
    </Wrapper>
  );
}
